/**
 * KnewtonResults Component
 *
 * Step 3 of Parlay Workshop - Display Knewton's recommended parlay
 * Shows legs, summary, correlation checks, and actions
 */

import React, { useMemo, useState } from 'react';
import LegCard from './LegCard';

const VIBE_LABELS = {
  safe: { emoji: '🐢', name: 'Safe' },
  smart: { emoji: '🎯', name: 'Smart' },
  hot: { emoji: '🔥', name: 'Hot' },
  surprise: { emoji: '🎲', name: 'Surprise' }
};

export default function KnewtonResults({
  results,
  vibe,
  onSwapLeg,
  onRemoveLeg,
  onBuildAnother,
  onBack
}) {
  const [copySuccess, setCopySuccess] = useState(false);

  const vibeInfo = VIBE_LABELS[vibe] || VIBE_LABELS.smart;

  // Calculate combined confidence
  const combinedConfidence = useMemo(() => {
    if (!results?.legs?.length) return 0;
    const product = results.legs.reduce((acc, leg) => acc * (leg.confidence / 100), 1);
    return Math.round(product * 100);
  }, [results]);

  // Calculate total odds
  const totalOdds = useMemo(() => {
    if (!results?.legs?.length) return 0;

    // Convert American odds to decimal, multiply, convert back
    const decimalOdds = results.legs.map(leg => {
      const odds = leg.odds;
      if (odds > 0) return (odds / 100) + 1;
      return (100 / Math.abs(odds)) + 1;
    });

    const combinedDecimal = decimalOdds.reduce((acc, odds) => acc * odds, 1);

    // Convert back to American
    if (combinedDecimal >= 2) {
      return Math.round((combinedDecimal - 1) * 100);
    } else {
      return Math.round(-100 / (combinedDecimal - 1));
    }
  }, [results]);

  // Calculate potential payout
  const calculatePayout = (betAmount) => {
    if (totalOdds > 0) {
      return betAmount + (betAmount * (totalOdds / 100));
    } else {
      return betAmount + (betAmount * (100 / Math.abs(totalOdds)));
    }
  };

  const handleCopyToBetslip = () => {
    // Format parlay for copying
    const parlayText = results.legs.map((leg, idx) =>
      `${idx + 1}. ${leg.player_name} ${leg.direction} ${leg.line} ${leg.stat_type.toUpperCase()}`
    ).join('\n');

    navigator.clipboard.writeText(parlayText).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  if (!results || !results.legs?.length) {
    return (
      <div className="glass-card p-8 text-center">
        <div className="text-4xl mb-4">🤔</div>
        <p className="text-text-secondary mb-4">
          No recommendations available. Try different settings.
        </p>
        <button
          onClick={onBack}
          className="btn-secondary px-4 py-2"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card p-4">
        <div className="flex items-start gap-3">
          <span className="text-3xl">🤖</span>
          <div>
            <h2 className="text-lg font-bold text-text-primary">
              KNEWTON'S {vibeInfo.emoji} {vibeInfo.name.toUpperCase()} PICKS FOR YOU
            </h2>
            <p className="text-text-secondary">
              Here's a +{totalOdds} {vibeInfo.name} parlay with {combinedConfidence}% combined conf
            </p>
          </div>
        </div>
      </div>

      {/* Legs */}
      <div className="space-y-4">
        {results.legs.map((leg, idx) => (
          <LegCard
            key={leg.id || idx}
            leg={leg}
            legNumber={idx + 1}
            onSwap={(newLeg) => onSwapLeg(idx, newLeg)}
            onRemove={() => onRemoveLeg(idx)}
            isOnlyLeg={results.legs.length === 1}
          />
        ))}
      </div>

      {/* Parlay Summary */}
      <div className="glass-card p-4">
        <h3 className="text-sm font-medium text-text-primary mb-4 uppercase tracking-wide">
          Parlay Summary
        </h3>

        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center p-3 bg-bg-card rounded-lg">
            <div className="text-2xl font-bold text-text-primary">{results.legs.length}</div>
            <div className="text-xs text-text-secondary">Legs</div>
          </div>
          <div className="text-center p-3 bg-bg-card rounded-lg">
            <div className="text-2xl font-bold text-accent-green">
              +{totalOdds}
            </div>
            <div className="text-xs text-text-secondary">Odds</div>
          </div>
          <div className="text-center p-3 bg-bg-card rounded-lg">
            <div className="text-2xl font-bold text-text-primary">
              ${calculatePayout(10).toFixed(2)}
            </div>
            <div className="text-xs text-text-secondary">$10 wins</div>
          </div>
        </div>

        {/* Stats */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-secondary">Combined Confidence:</span>
            <span className={`font-bold ${
              combinedConfidence >= 50 ? 'text-accent-green' :
              combinedConfidence >= 30 ? 'text-yellow-500' :
              'text-red-400'
            }`}>
              {combinedConfidence}%
            </span>
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-text-secondary">Correlation Check:</span>
            {results.correlation_warnings?.length > 0 ? (
              <span className="text-yellow-500">
                ⚠️ {results.correlation_warnings.length} warning{results.correlation_warnings.length !== 1 ? 's' : ''}
              </span>
            ) : (
              <span className="text-accent-green">✅ No conflicts</span>
            )}
          </div>

          {results.historical_hit_rate !== undefined && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-secondary">Similar parlays hit:</span>
              <span className="font-medium text-text-primary">
                {results.historical_hit_rate}% historically
              </span>
            </div>
          )}
        </div>

        {/* Correlation warnings */}
        {results.correlation_warnings?.length > 0 && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4">
            <p className="text-sm font-medium text-yellow-400 mb-2">⚠️ Correlation Warnings:</p>
            <ul className="text-xs text-yellow-300 space-y-1">
              {results.correlation_warnings.map((warning, idx) => (
                <li key={idx}>• {warning}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Action buttons */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={handleCopyToBetslip}
            className="py-3 px-4 rounded-lg bg-accent-green text-white font-medium hover:bg-accent-green/90 transition-colors"
          >
            {copySuccess ? '✓ Copied!' : '📋 Copy to Betslip'}
          </button>
          <button
            onClick={onBuildAnother}
            className="py-3 px-4 rounded-lg bg-bg-card text-text-primary font-medium hover:bg-bg-card-hover transition-colors"
          >
            🔄 Build Another
          </button>
        </div>
      </div>

      {/* Knewton's final thoughts */}
      <div className="glass-card p-4">
        <div className="flex items-start gap-3">
          <span className="text-2xl">💡</span>
          <div className="text-sm text-text-secondary">
            <span className="font-medium text-text-primary">Knewton's take: </span>
            {combinedConfidence >= 50 ? (
              "This parlay has solid fundamentals. Each leg is data-backed."
            ) : combinedConfidence >= 30 ? (
              "Moderate risk. Some legs depend on volatile factors - manage your stake."
            ) : (
              "High risk, high reward. Only bet what you can afford to lose."
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
